import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

type Client = { name: string; logo: string; small?: boolean; large?: boolean };

const clients: Client[] = [
  { name: 'Andritz', logo: '/clients/client-01-andritz.png' },
  { name: 'BHEL', logo: '/clients/client-02-bhel.png' },
  { name: 'Daniel', logo: '/clients/client-03-daniel.png' },
  { name: 'Client 4', logo: '/clients/client-04.png' },
  { name: 'Client 5', logo: '/clients/client-05.png' },
  { name: 'Client 6', logo: '/clients/client-06.png' },
  { name: 'Client 7', logo: '/clients/client-07.png' },
  { name: 'Client 8', logo: '/clients/client-08.png' },
  { name: 'Client 9', logo: '/clients/client-09.png' },
  { name: 'Client 10', logo: '/clients/client-10.png' },
  { name: 'Client 11', logo: '/clients/client-11.png' },
  { name: 'Client 12', logo: '/clients/client-12.png' },
  { name: 'Client 13', logo: '/clients/client-13.png' },
  { name: 'Client 14', logo: '/clients/client-14.png' },
  { name: 'Client 15', logo: '/clients/client-15.png', large: true },
  { name: 'Client 16', logo: '/clients/client-16.png', large: true },
  { name: 'Client 17', logo: '/clients/client-17.png', small: true },
  { name: 'Client 18', logo: '/clients/client-18.png', small: true },
];

export const ClientLogosMarquee: React.FC = () => {
  const { isDark } = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const autoScroll = useCallback(() => {
    if (scrollRef.current && !isPaused) {
      const container = scrollRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (container.scrollLeft >= maxScroll - 1) {
        container.scrollLeft = 0;
      } else {
        container.scrollLeft += 1;
      }
    }
  }, [isPaused]);

  useEffect(() => {
    const interval = setInterval(autoScroll, 30);
    return () => clearInterval(interval);
  }, [autoScroll]);

  const pauseAutoScroll = () => {
    setIsPaused(true);
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      pauseAutoScroll();
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -300 : 300,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className={`w-full pt-12 md:pt-16 pb-6 md:pb-8 relative overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-vmil-navy' : 'bg-white'
    }`}>
      <div className="container mx-auto px-4">
        <h2 className={`text-2xl sm:text-3xl md:text-4xl font-extrabold mb-8 md:mb-12 text-center uppercase tracking-tight ${
          isDark ? 'text-white' : 'text-vmil-blue'
        }`}>
          TRUSTED BY GLOBAL INDUSTRY LEADERS
        </h2>

        <div className="relative">
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full shadow-lg transition-all hover:scale-110 ${
              isDark ? 'bg-vmil-blue text-white hover:bg-vmil-blue/90' : 'bg-vmil-blue text-white hover:bg-vmil-blue/90'
            }`}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 p-2 sm:p-3 rounded-full shadow-lg transition-all hover:scale-110 ${
              isDark ? 'bg-vmil-blue text-white hover:bg-vmil-blue/90' : 'bg-vmil-blue text-white hover:bg-vmil-blue/90'
            }`}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          <div className={`absolute left-8 sm:left-12 top-0 bottom-0 w-8 sm:w-16 z-10 pointer-events-none ${
            isDark ? 'bg-gradient-to-r from-vmil-navy to-transparent' : 'bg-gradient-to-r from-white to-transparent'
          }`} />
          <div className={`absolute right-8 sm:right-12 top-0 bottom-0 w-8 sm:w-16 z-10 pointer-events-none ${
            isDark ? 'bg-gradient-to-l from-vmil-navy to-transparent' : 'bg-gradient-to-l from-white to-transparent'
          }`} />

          <div
            ref={scrollRef}
            className="flex items-center gap-4 sm:gap-8 md:gap-12 py-4 px-10 sm:px-16 overflow-x-auto"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {clients.map((client, index) => (
              <div key={index} className="flex-shrink-0 p-2 sm:p-4 transition-all hover:scale-110 flex items-center">
                <img
                  src={client.logo}
                  alt={client.name}
                  className={`w-auto object-contain transition-all ${
                    client.small ? 'h-8 sm:h-10 md:h-12 lg:h-14' :
                    client.large ? 'h-14 sm:h-18 md:h-24 lg:h-28' : 'h-12 sm:h-16 md:h-20 lg:h-24'
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
